async function exportXLSX(results, numQ, sessionId) {
  const XLSX = await import("xlsx");
  const header = ["Grupp", ...Array.from({ length: numQ }, (_, i) => `Q${i + 1}`), "Totalt"];
  const rows = results.map(r => [r.groupName, ...Array.from({ length: numQ }, (_, i) => r.scores?.[i] ?? ""), r.total]);
  if (results.length > 1) {
    const avg = ["Snitt", ...Array.from({ length: numQ }, (_, qi) => parseFloat((results.reduce((s, r) => s + (r.scores?.[qi] ?? 0), 0) / results.length).toFixed(1))), parseFloat((results.reduce((s, r) => s + r.total, 0) / results.length).toFixed(1))];
    rows.push(avg);
  }
  const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
  ws["!cols"] = [{ wch: 22 }, ...Array(numQ).fill({ wch: 6 }), { wch: 8 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Resultat");
  XLSX.writeFile(wb, `tRAT-${sessionId}.xlsx`);
}

async function exportPDF(results, numQ, sessionId, qStats, answerKey, labels) {
  const { jsPDF } = await import("jspdf");
  // jspdf-autotable v5 exporterar en funktion — autoTable(doc, opts) — i stället
  // för att montera doc.autoTable som v3 gjorde.
  const { default: autoTable } = await import("jspdf-autotable");
  const doc = new jsPDF({ orientation: numQ > 12 ? "landscape" : "portrait", unit: "mm" });

  // Header
  doc.setFontSize(16); doc.setFont("helvetica", "bold");
  doc.text("tRAT — Resultat", 14, 18);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.setTextColor(120); doc.text(`Session: ${sessionId}  ·  ${new Date().toLocaleDateString("sv-SE")}`, 14, 25);
  doc.setTextColor(0);

  // Scores table
  const sorted = [...results].sort((a, b) => b.total - a.total);
  const head = [["Grupp", ...Array.from({ length: numQ }, (_, i) => `Q${i + 1}`), "Tot"]];
  const body = sorted.map(r => [r.groupName, ...Array.from({ length: numQ }, (_, i) => r.scores?.[i] ?? "—"), r.total]);
  if (results.length > 1) body.push(["Snitt", ...Array.from({ length: numQ }, (_, qi) => (results.reduce((s, r) => s + (r.scores?.[qi] ?? 0), 0) / results.length).toFixed(1)), (results.reduce((s, r) => s + r.total, 0) / results.length).toFixed(1)]);

  autoTable(doc, {
    startY: 30, head, body,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [30, 34, 53], textColor: 245 },
    columnStyles: { 0: { cellWidth: 36 } },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index > 0 && d.column.index <= numQ) {
        const v = Number(d.cell.raw);
        if (v === 4) d.cell.styles.textColor = [34, 197, 94];
        else if (v === 2) d.cell.styles.textColor = [245, 200, 66];
        else if (v === 0 && d.cell.raw !== "—") d.cell.styles.textColor = [255, 107, 107];
      }
    },
  });

  // Q analysis table
  if (qStats && qStats.length > 0) {
    const y2 = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.text("Frågeanalys", 14, y2);
    const qHead = [["Fråga", "Snittpoäng", "1:a försök %", "Diskriminering", "Rätt svar"]];
    const qBody = qStats.map(({ qi, avgScore, firstTries, disc }) => [`Q${qi + 1}`, avgScore.toFixed(1), results.length > 0 ? Math.round(firstTries / results.length * 100) + "%" : "—", results.length >= 2 ? disc.toFixed(2) : "—", labels[answerKey[qi]]]);
    autoTable(doc, { startY: y2 + 4, head: qHead, body: qBody, styles: { fontSize: 8 }, headStyles: { fillColor: [30, 34, 53], textColor: 245 } });
  }

  doc.save(`tRAT-${sessionId}.pdf`);
}

function openStudentResultSheet(session, groupName, states, lang) {
  const esc = (s) => String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  const sv = lang === 'sv';
  const total = states.reduce((s, q) => s + (q.score ?? 0), 0);
  const max = session.numQ * 4;
  const pct = max > 0 ? Math.round((total / max) * 100) : 0;
  const counts = { 4: 0, 2: 0, 1: 0, 0: 0 };
  states.forEach(q => { if (q.score !== null) counts[q.score] = (counts[q.score] || 0) + 1; });
  const now = new Date();
  const dateStr = now.toLocaleDateString('sv-SE') + ' ' + now.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

  const questionRows = states.map((s, qi) => {
    const pts = s.score ?? 0;
    const attempts = s.attempts?.length || 0;
    const barColor = pts === 4 ? '#15803d' : pts === 2 ? '#c88a0a' : pts === 1 ? '#a67208' : '#dc2626';
    return '<tr>' +
      '<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;color:#344054;">Q' + (qi + 1) + '</td>' +
      '<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">' +
        '<span style="display:inline-block;background:' + barColor + '18;color:' + barColor + ';border:1px solid ' + barColor + '44;border-radius:999px;padding:2px 12px;font-weight:600;font-size:13px;">+' + pts + '</span>' +
      '</td>' +
      '<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;color:#667085;">' + attempts + '</td>' +
    '</tr>';
  }).join('');

  const title = esc(session.sessionTitle || ('tRAT ' + session.sessionId));
  const safeGroup = esc(groupName);
  const safeSessionId = esc(session.sessionId);
  const docTitle = (sv ? 'Resultat' : 'Results') + ' – ' + safeGroup + ' – ' + title;

  const html = '<!DOCTYPE html><html lang="' + lang + '"><head><meta charset="UTF-8"><title>' + docTitle + '</title>' +
    '<link rel="preconnect" href="https://fonts.googleapis.com">' +
    '<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">' +
    '<style>' +
    '*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }' +
    'body { font-family: "Plus Jakarta Sans", sans-serif; background: #fff; color: #101828; -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 40px; max-width: 600px; margin: 0 auto; }' +
    '@media print { body { padding: 20px; } .no-print { display: none !important; } }' +
    '.header { text-align: center; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }' +
    '.score-big { font-family: "DM Mono", monospace; font-size: 56px; font-weight: 700; line-height: 1; }' +
    '.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 20px 0; }' +
    '.stat-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 6px; text-align: center; }' +
    '.stat-num { font-family: "DM Mono", monospace; font-size: 20px; font-weight: 600; color: #c88a0a; }' +
    '.stat-label { font-size: 11px; color: #667085; margin-top: 3px; }' +
    'table { width: 100%; border-collapse: collapse; margin-top: 24px; }' +
    'th { padding: 8px 12px; text-align: left; font-size: 12px; color: #667085; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e5e7eb; }' +
    '.footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #98a2b3; }' +
    '</style></head><body>' +
    '<div class="header">' +
      '<div style="font-size:12px;color:#98a2b3;margin-bottom:8px;font-family:\'DM Mono\',monospace;">tRAT</div>' +
      '<div style="font-size:18px;font-weight:700;color:#101828;margin-bottom:4px;">' + title + '</div>' +
      '<div style="font-size:14px;color:#667085;margin-bottom:16px;">Session: ' + safeSessionId + '</div>' +
      '<div style="font-size:14px;color:#344054;font-weight:600;margin-bottom:20px;">' + safeGroup + '</div>' +
      '<div class="score-big" style="color:' + (pct >= 70 ? '#15803d' : pct >= 40 ? '#c88a0a' : '#dc2626') + ';">' + total + '</div>' +
      '<div style="font-size:14px;color:#667085;font-family:\'DM Mono\',monospace;">' + (sv ? 'av' : 'of') + ' ' + max + ' ' + (sv ? 'poäng' : 'points') + ' (' + pct + '%)</div>' +
    '</div>' +
    '<div class="stats-grid">' +
      '<div class="stat-box"><div class="stat-num">' + (counts[4] || 0) + '×</div><div class="stat-label">' + (sv ? '1:a försök' : '1st try') + '</div></div>' +
      '<div class="stat-box"><div class="stat-num">' + (counts[2] || 0) + '×</div><div class="stat-label">' + (sv ? '2:a försök' : '2nd try') + '</div></div>' +
      '<div class="stat-box"><div class="stat-num">' + (counts[1] || 0) + '×</div><div class="stat-label">' + (sv ? '3:e försök' : '3rd try') + '</div></div>' +
      '<div class="stat-box"><div class="stat-num" style="color:#dc2626;">' + (counts[0] || 0) + '×</div><div class="stat-label">' + (sv ? 'Miss' : 'Miss') + '</div></div>' +
    '</div>' +
    '<table>' +
      '<thead><tr>' +
        '<th>' + (sv ? 'Fråga' : 'Question') + '</th>' +
        '<th style="text-align:center;">' + (sv ? 'Poäng' : 'Score') + '</th>' +
        '<th style="text-align:center;">' + (sv ? 'Försök' : 'Attempts') + '</th>' +
      '</tr></thead>' +
      '<tbody>' + questionRows + '</tbody>' +
    '</table>' +
    '<div class="footer">' +
      dateStr + '<br>' +
      'tRAT · aktivtlarande.se' +
    '</div>' +
    '<div class="no-print" style="text-align:center;margin-top:24px;">' +
      '<button onclick="window.print()" style="background:#1d4ed8;color:#fff;border:none;border-radius:8px;padding:10px 24px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;">⎙ ' + (sv ? 'Skriv ut / Spara PDF' : 'Print / Save PDF') + '</button>' +
    '</div>' +
    '</body></html>';

  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

export { exportXLSX, exportPDF, openStudentResultSheet };
